import type { ReactNode } from 'react';

import { Paper, type SxProps, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';


type EntityCardProps = {
	children: ReactNode;
	sx?: SystemStyleObject<Theme>;
	onClick?: () => void;
};

/** Shared elevated card shell for gallery-style app and context lists. */
export default function EntityCard({ children, sx, onClick }: EntityCardProps) {
	const baseSx: SystemStyleObject<Theme> = {
		display: 'flex',
		flexDirection: 'column',
		borderColor: 'divider',
		backgroundColor: 'background.paper',
		backgroundImage: (theme) => (theme.palette.mode === 'dark' ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))' : 'none'),
		boxShadow: 1,
		transition: 'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
		'&:hover': { borderColor: 'primary.main', boxShadow: 3 },
	};
	const resolvedSx: SxProps<Theme> = sx === undefined ? baseSx : [baseSx, sx];

	return (
		<Paper variant="outlined" onClick={onClick} sx={resolvedSx}>
			{children}
		</Paper>
	);
}

type EntityCardPlaceholderProps = {
	icon: ReactNode;
	title: ReactNode;
	description: ReactNode;
	action: ReactNode;
	onClick: () => void;
	sx?: SystemStyleObject<Theme>;
};

/** Shared create-card treatment for entity galleries. */
export function EntityCardPlaceholder({ icon, title, description, action, onClick, sx }: EntityCardPlaceholderProps) {
	const baseSx: SystemStyleObject<Theme> = {
		alignItems: 'center',
		justifyContent: 'center',
		gap: 1,
		p: 2,
		borderStyle: 'dashed',
		textAlign: 'center',
		cursor: 'pointer',
		'& > *': { opacity: 0.5, transition: 'opacity 150ms ease' },
		'&:hover': {
			backgroundColor: 'action.hover',
			borderColor: 'primary.main',
			boxShadow: 3,
			'& > *': { opacity: 1 },
		},
	};
	const resolvedSx: SystemStyleObject<Theme> = sx === undefined ? baseSx : { ...baseSx, ...sx };

	return (
		<EntityCard onClick={onClick} sx={resolvedSx}>
			{icon}
			{title}
			{description}
			{action}
		</EntityCard>
	);
}
